<template>
<PageView :loading="loading" :logo="logo" header-class="bg-light" class="bg-light">
  <ScrollDownIndicatorComponent ref="scrollDownIndicator" />
  <div class="card" v-if="participationValue && participation">
    <div class="card-header object-fit-cover d-none d-md-block">
      <StepperComponent class="d-flex"
      :step="currentStepIndex"
      @on-step-changed="navStepChanged"
      :can-navigate-to-step="canNavigateToStep"
      :steps="steps" />
    </div>
    <div id="registration-main-card" class="card-body pt-0 px-0">
      <TravelBannerComponent :participation="participation" />
      <div class="row justify-content-center">
        <div class="col-11 col-md-10">
          <h2 class="card-title text-center my-4 d-flex justify-content-between flex-column flex-md-row">
            <div class="col text-start">
              <button v-if="!isFirstStep && !paymentLocked" class="btn btn-outline-primary text-nowrap d-none d-md-block" @click="previousStep" :class="{ 'disabled': isSubmitting }">
                <i class="fa-solid fa-angle-left me-2"></i> Précédent
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" ></span>
              </button>
            </div>
            <div class="">{{ steps.length > currentStepIndex ? steps[currentStepIndex]!.title : '' }}</div>
            <div class="col text-end pt-2 pt-md-0 d-flex justify-content-md-end" :class="[!isFirstStep ? 'justify-content-between' : 'justify-content-end']">
              <button v-if="!isFirstStep && !paymentLocked" class="btn btn-outline-primary text-nowrap d-md-none" @click="previousStep" :class="{ 'disabled': isSubmitting }">
                <i class="fa-solid fa-angle-left me-2"></i> Précédent
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" ></span>
              </button>
              <div v-if="participation.travel.isoCode" class="flag-wrapper">
                <span class="fi" :class="['fi-' + participation.travel.isoCode.toLowerCase()]"></span>
              </div>
              <!-- <button v-if="!isLastStep && currentPage" class="btn btn-primary text-nowrap" @click="nextStep" :class="{ 'disabled': isSubmitting }">Suivant <i class="fa-solid fa-angle-right ms-2"></i></button> -->
            </div>
          </h2>
          <Transition name="fade" mode="out-in">
            <div v-if="currentStep?.key == 'infos'">
              <TravelEditInfos v-model="participationValue" v-model:is-mobile-phone-valid="isMobilePhoneValid" v-model:is-emergency-mobile-phone-valid="isEmergencyMobilePhoneValid" />
            </div>
            <div v-else-if="currentStep?.key == 'invoice'">
              <TravelEditInvoice v-model="participationValue" />
            </div>
            <div v-else-if="currentStep?.key == 'companions'">
              <TravelEditCompanions v-model="participationValue" />
            </div>
            <div v-else-if="currentStep?.key == 'transport'">
              <TravelEditTransport v-model="participationValue" />
            </div>
            <div v-else-if="currentStep?.key == 'transfers'">
              <TravelEditTransfer v-model="participationValue" />
            </div>
            <div v-else-if="currentStep?.key == 'hotels'">
              <TravelEditHotels v-model="participationValue" />
            </div>
            <div v-else-if="currentStep?.key == 'options'">
              <TravelEditOptions v-model="participationValue" />
            </div>
            <div v-else-if="currentStep?.key == 'documents'">
              <TravelEditDocuments v-model="participationValue" />
            </div>
            <div v-else-if="currentStep?.key == 'signature'">
              <TravelEditSignature v-model="participationValue" ref="travelEditSignatureInstance" />
            </div>
            <div v-else-if="currentStep?.key == 'requests'">
              <TravelEditSpecialRequests v-model="participationValue" />
            </div>
            <div v-else-if="currentStep?.key == 'summary'">
              <TravelEditSummary v-model="participationValue" />
            </div>
            <div v-else-if="currentStep?.key == 'payment'">
              <TravelEditPayment v-model="participationValue" />
            </div>
            <div v-else-if="currentStep?.key == 'admin-data'">
              <TravelEditForAdmin v-model="participationValue" />
            </div>
            <div v-else-if="currentPage">
              <div v-html="normalizeRichTextContent(currentPage.content)" class="ql-editor p-0"></div>
            </div>
          </Transition>
          <div v-if="!isLastStep || canFinalize" class="mt-3" :class="[isLastStep ? 'text-center': 'text-end']">
            <button v-if="isLastStep && participation.travel.paymentEnabled" @click="goHome" class="btn btn-outline-success">
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" ></span>
              <i class="fa-regular fa-house ms-2"></i> Retour à l'accueil
            </button>
            <button v-else-if="isLastStep" class="btn btn-lg btn-warning text-nowrap" @click="nextStep" :disabled="isSubmitting || !canFinalize" :class="{ 'disabled': isSubmitting || !canFinalize }">
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" ></span>
              Finaliser mon inscription<i class="fa-solid fa-check ms-2"></i>
            </button>
            <button v-else-if="travelEditSignatureInstance == null || travelEditSignatureInstance.isValidated" class="btn btn-primary text-nowrap" @click="nextStep" :class="{ 'disabled': isSubmitting }">
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" ></span>
              Suivant<i class="fa-solid fa-angle-right ms-2"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</PageView>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed, useTemplateRef } from 'vue';
import { GetCompanionLabel, getPictureUrl, normalizeRichTextContent, ScrollToTop } from '@/compositions/helper';
import travelApi from '@/api/travelApi'
import PageView from '@/views/PageView.vue';
import TravelEditInfos from '@/views/user/travel/TravelEditInfos.vue';
import TravelEditInvoice from '@/views/user/travel/TravelEditInvoice.vue';
import TravelEditCompanions from '@/views/user/travel/TravelEditCompanions.vue';
import TravelEditTransport from '@/views/user/travel/TravelEditTransport.vue';
import TravelEditTransfer from '@/views/user/travel/TravelEditTransfer.vue';
import TravelEditHotels from '@/views/user/travel/TravelEditHotels.vue';
import TravelEditOptions from '@/views/user/travel/TravelEditOptions.vue';
import TravelEditSummary from '@/views/user/travel/TravelEditSummary.vue';
import TravelEditSpecialRequests from '@/views/user/travel/TravelEditSpecialRequests.vue';
import TravelEditDocuments from '@/views/user/travel/TravelEditDocuments.vue';
import TravelEditForAdmin from '@/views/user/travel/TravelEditForAdmin.vue';
import TravelEditSignature from '@/views/user/travel/TravelEditSignature.vue';
import TravelEditPayment from '@/views/user/travel/TravelEditPayment.vue';
import ScrollDownIndicatorComponent from '@/components/common/ScrollDownIndicatorComponent.vue';
import * as zod from 'zod';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { useNotificationsStore } from '@/stores/notificationStore';
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/userStore';
import { useTravelConfigStore } from '@/stores/travelConfigStore';

const notificationsStore = useNotificationsStore();
const router = useRouter()
const userStore = useUserStore();
const travelConfigStore = useTravelConfigStore();

const scrollDownIndicator = ref<InstanceType<typeof ScrollDownIndicatorComponent>>()

const loading = ref(true);
const step = ref(0);
const isMobilePhoneValid = ref(true);
const isEmergencyMobilePhoneValid = ref(true);

const props = defineProps<{id?: number, slug?: string}>()

const participation = ref<UserParticipation|null>(null);

const validatedSteps = ref(new Set<number>());
const saveInProgress = ref(false);

const travelEditSignatureInstance = useTemplateRef<InstanceType<typeof TravelEditSignature>>('travelEditSignatureInstance')

const paymentRequired = computed(() => {
  return participation.value?.travel.paymentEnabled === true && !userStore.isImpersonated;
});

const steps = computed<StepperElement[]>(() => {
  const internalSteps: StepperElement[] =
  [
    {
      key: 'infos',
      title: 'Informations',
      stepTitle: 'Informations',
      icon: 'fa-solid fa-info'
    },
    {
      key: 'invoice',
      title: 'Facturation',
      stepTitle: 'Facturation',
      icon: 'fa-solid fa-receipt',
      active: (participation.value?.travel.invoiceEnabled??false) === true
    },
    {
      key: 'companions',
      title: GetCompanionLabel(participation.value?.travel.companionLabel, 2, true),
      stepTitle: GetCompanionLabel(participation.value?.travel.companionLabel, 2, true),
      icon: 'fa-solid fa-user-group',
      active: (participation.value?.travel.companionsEnabled??false) === true
    },
    {
      key: 'transport',
      title: 'Transport',
      stepTitle: 'Transport',
      icon: 'fa-solid fa-plane',
      active: ((participation.value?.travel.transportations?.length??0) > 0)
    },
    {
      key: 'transfers',
      title: 'Transferts',
      stepTitle: 'Transferts',
      icon: 'fa-solid fa-bus',
      visible: (participation.value?.travel.transfers?.length??0) > 0 && (participation.value?.travel.packageMandatory === true),
      active:
           (participation.value?.travel.transfers?.length??0) > 0
        &&
        (
          (
              ( participation.value?.travelInfo?.withoutTransport === true || participation.value?.travelInfo?.withPackage === false )
           && (
               participation.value?.transportation?.outboundDate?.toDateString() == participation.value?.travel.startDate.toDateString()
            || participation.value?.transportation?.returnDate?.toDateString() == participation.value?.travel.endDate.toDateString()

           )
          )
          || (participation.value?.travel.packageMandatory === true)
        )
        // && (
          //    ((participation.value?.travelInfo?.withPackage??true) === false)
          // || ((participation.value?.travelInfo?.specialCase??false) === true)
          // ||
          //  ((participation.value?.travelInfo?.withoutTransport??false) === true)
        // )
    },
    {
      key: 'hotels',
      title: 'Hébergement',
      stepTitle: 'Hébergement',
      icon: 'fa-solid fa-hotel',
      active: (participation.value?.travel.hotels?.length??0) > 0
    },
    {
      key: 'options',
      title: 'Options',
      stepTitle: 'Options',
      icon: 'fa-solid fa-person-snowboarding',
      active: (participation.value?.travel.options?.length ?? 0) > 0
    },
    {
      key: 'documents',
      title: 'Documents',
      stepTitle: 'Documents',
      icon: 'fa-solid fa-file-arrow-up',
      active: (participation.value?.documents?.length ?? 0) > 0
    },
    {
      key: 'signature',
      title: 'Signature',
      stepTitle: 'Signature',
      icon: 'fa-solid fa-pen-nib',
      active: !!participation.value?.travel.firmaTemplateId
    },
    {
      key: 'requests',
      title: 'Demandes particulières',
      stepTitle: 'Demandes',
      icon: 'fa-solid fa-bell-concierge',
      visible: false
    },
    ...(participation.value?.travel.pages?.filter(page => !page.hideDuringInscription).map(page => ({
      key: `page-${page.id}`,
      title: page.title ?? "",
      stepTitle: ((page.labelIcon ?? page.title ?? "").length > 30 ? ((page.labelIcon ?? page.title ?? "").substring(0,30) + "...") : (page.labelIcon ?? page.title ?? "")),
      icon: 'fa-solid fa-globe'
    })) ?? []),
    {
      key: 'admin-data',
      title: 'Données admin',
      stepTitle: 'Admin',
      icon: 'fa-solid fa-screwdriver-wrench',
      active: userStore.connectedUser?.isAdmin
    },
    {
      key: 'summary',
      title: 'Récapitulatif',
      stepTitle: 'Récapitulatif',
      icon: 'fa-solid fa-list'
    },
    {
      key: 'payment',
      title: 'Paiement',
      stepTitle: 'Paiement',
      icon: 'fa-solid fa-credit-card',
      active: participation.value?.travel.paymentEnabled === true
    }
  ]

  // internalSteps.forEach((internalStep,idx) => {
  //   if(validatedSteps.value.has(idx)) {
  //     internalStep.icon = 'fa-solid fa-check';
  //   }
  // });

  return internalSteps;
})

const schemas = [
  zod.object({
      userInfo: zod.object({
        civility: zod.nativeEnum(CivilityEnum, {message: 'La civilité est obligatoire' }),
        firstName: zod.string({message: 'Le prénom est obligatoire'}).min(1, {message: 'Le prénom est obligatoire'}),
        lastName: zod.string({message: 'Le nom est obligatoire'}).min(1, {message: 'Le nom est obligatoire'}),
        email: zod.string({message: 'L\'email est obligatoire'}).email('Email invalide'),
        mobilePhone: zod.string({message: 'Le téléphone est obligatoire' }).refine(() => isMobilePhoneValid.value, {message: "Le numéro de téléphone mobile est invalide"} ),
        birthDate: zod.date({message: 'La date de naissance est obligatoire' })
          .min(new Date(1900, 0, 1), "Date de naissance invalide")
          .optional()
          .refine(
            (val) => val !== undefined || participation.value?.travel.birthDateRequired === false,
            { message: 'La date de naissance est obligatoire' }
          ),
        nationality: zod.string().optional().refine(
          (val) => !(participation.value?.travel.nationalityRequired ?? false) || (val !== undefined && val.length > 0),
          { message: 'La nationalité est obligatoire' }
        ),
        address: zod.string().optional().refine(
          (val) => !(participation.value?.travel.addressRequired ?? false) || (val !== undefined && val.length > 0),
          { message: 'L\'adresse est obligatoire' }
        ),
        city: zod.string().optional().refine(
          (val) => !(participation.value?.travel.addressRequired ?? false) || (val !== undefined && val.length > 0),
          { message: 'La ville est obligatoire' }
        ),
        postalCode: zod.string().optional().refine(
          (val) => !(participation.value?.travel.addressRequired ?? false) || (val !== undefined && val.length > 3),
          { message: 'Le code postal est obligatoire' }
        ),
        passportNumber: zod
          .string()
          .min(5, "Numéro de passeport trop court")
          .max(50, "Numéro de passeport trop long")
          .nullish()
          .or(zod.literal('')),
        passportExpirationDate: zod
            .date({message: 'La date d\'expiration du passeport est obligatoire' })
            .min(participation.value?.travel.endDate ?? new Date(), {message: 'Le passeport doit etre valide à la date du retour' })
            .nullish(),
        estaNumber: zod
          .string()
          .min(2, 'Numéro ESTA trop court')
          .max(50, 'Numéro ESTA trop long')
          .nullish()
          .or(zod.literal('')),
        status: zod
            .nativeEnum(UserStatusEnum, {message: 'Veuillez indiquer votre statut' })
            .optional().refine((status) => status !== undefined || (participation.value?.travel.userStatusRequired ?? false) == false, 'Veuillez indiquer votre statut'),
        emergencyContactFirstname: zod.string().optional(),
        emergencyContactLastname: zod.string().optional(),
        emergencyContactEmail: zod.string().email('Email de la personne à prévenir invalide').optional(),
        emergencyContactPhone: zod.string().optional(),//.refine(() => isEmergencyMobilePhoneValid.value, {message: "Le numéro de téléphone du contact à prévenir est invalide"} ),
        infoFields: zod.array(zod.object({
          id: zod.number().nonnegative(),
          label: zod.string(),
          required: zod.boolean(),
          value: zod.union([zod.string(), zod.date()]).optional(),
          })).optional()
            .superRefine((data, ctx) => {
              data?.forEach((field, idx) => {
                if(field.required && (!field.value || (typeof field.value == typeof "string"  && (field.value as string).trim().length == 0) || (field.value instanceof Date && field.value !== undefined))) {
                  ctx.addIssue({
                    code: zod.ZodIssueCode.custom,
                    message: `L'information "${field.label}" est obligatoire`,
                    path: [idx, 'value']
                  })
                }
              })
            })
        })
        .refine((data) => {
          return (!data.passportExpirationDate && (data.passportNumber??'').length > 0) ? false : true;
        }, {message: 'Veuillez entrer une date d\'expiration pour le passeport'}),
    }),
    zod.object({
      invoice: zod.object({
        company: zod.string({message: 'La société est obligatoire'}).min(1, {message: 'La société est obligatoire'}),
        shopCode: zod.string({message: 'Le numéro de magasin est obligatoire'}).min(1, {message: 'Le numéro de magasin est obligatoire'}),
        lastName: zod.string({message: 'Le nom est obligatoire'}).min(1, {message: 'Le nom est obligatoire'}),
        firstName: zod.string({message: 'Le prénom est obligatoire'}).min(1, {message: 'Le prénom est obligatoire'}),
        address: zod.string({message: 'L\'adresse de facturation est obligatoire'}).min(1, {message: 'L\'adresse est obligatoire'}),
        zipCode: zod.string({message: 'Le code postal est obligatoire'}).min(1, {message: 'Le code postal est obligatoire'}),
        city: zod.string({message: 'La ville est obligatoire'}).min(1, {message: 'La ville est obligatoire'}),
        country: zod.string({message: 'Le pays est obligatoire'}).min(1, {message: 'Le pays est obligatoire'}),
        vatNumber: zod.string().nullish(),
      })
    }),
    zod.object({
      companions: zod.array(zod.object({
        relation: zod.nativeEnum(UserRelationEnum, {message: 'La relation est obligatoire' }),
        status: zod
            .nativeEnum(UserStatusEnum, {message: 'Le statut est obligatoire' })
            .optional().refine((status) => status !== undefined || (participation.value?.travel.userStatusRequired ?? false) == false, 'Le statut est obligatoire'),
        civility: zod
            .nativeEnum(CivilityEnum, {message: 'La civilité est obligatoire' }),
        firstName: zod.string({message: 'Le prénom est obligatoire'}).min(1, {message: 'Le prénom est obligatoire'}),
        lastName: zod.string({message: 'Le nom est obligatoire'}).min(1, {message: 'Le nom est obligatoire'}),
        email: zod.string().email('Email invalide').nullish().or(zod.literal('')),
        mobilePhone: zod.string({message: 'Le téléphone est obligatoire' }).refine(() => isMobilePhoneValid.value, {message: "Le numéro de téléphone mobile est invalide"} ),
        birthDate: zod.date({message: 'La date de naissance est obligatoire' })
        .min(new Date(1900, 0, 1), "Date de naissance invalide")
        .optional()
          .refine(
            (val) => val !== undefined || participation.value?.travel.birthDateRequired === false,
            { message: 'La date de naissance est obligatoire' }
          ),
        nationality: zod.string().optional().refine(
          (val) => !(participation.value?.travel.nationalityRequired ?? false) || (val !== undefined && val.length > 0),
          { message: 'La nationalité est obligatoire' }
        ),
        passportNumber: zod
          .string()
          .min(5, "Numéro de passeport trop court")
          .max(50, "Numéro de passeport trop long")
          .nullish()
          .or(zod.literal('')),
        passportExpirationDate: zod
            .date({message: 'La date d\'expiration du passeport est obligatoire' })
            .min(participation.value?.travel.endDate ?? new Date(), {message: 'Le passeport doit etre valide à la date du retour' })
            .nullish(),
        estaNumber: zod
          .string()
          .min(5, 'Numéro ESTA trop court')
          .max(50, 'Numéro ESTA trop long')
          .nullish()
          .or(zod.literal('')),
        infoFields: zod.array(zod.object({
          id: zod.number().nonnegative(),
          label: zod.string(),
          required: zod.boolean(),
          value: zod.union([zod.string({message: 'Veuillez entrer une valeur' }), zod.date()]).optional(),
          })).optional()
        .superRefine((data, ctx) => {
          data?.forEach((field, idx) => {
            if(field.required && (!field.value || (typeof field.value == typeof "string"  && (field.value as string).trim().length == 0) || (field.value instanceof Date && field.value !== undefined))) {
              ctx.addIssue({
                code: zod.ZodIssueCode.custom,
                message: `L'information "${field.label}" est obligatoire`,
                path: [idx, 'value']
              })
            }
          })
        })
      }))
    }),
    zod.object({
      travelInfo: zod.object({
        withPackage: zod.boolean({message: 'Veuillez indiquer si vous prenez les dates du package ou non' }),
        withoutTransport: zod.boolean().optional(),
        specialCase: zod.boolean().optional(),
        customStartDate: zod.date().optional(),
        customEndDate: zod.date().optional(),
      }),
      transportation: zod.object({
        id: zod.number().nullish(),
        outboundDate: zod.date().nullish(),
        returnDate: zod.date().nullish(),
        optionId: zod.number().nullish()
      })
    }).superRefine((data, ctx) => {
      if((participation.value?.travel.transportations?.length??0) > 0) {
        if(data.travelInfo.withPackage) {
          if((!data.transportation.id || data.transportation.id <= 0) && data.travelInfo.withoutTransport !== true && data.travelInfo.specialCase !== true) {
            ctx.addIssue({
              code: zod.ZodIssueCode.custom,
              message: 'Veuillez sélectionner un transport',
              path: ['transportation','id']
            })
          }
          else if(participation.value?.travel.transportations?.some(t => t.options && t.options.length > 0) && (!data.transportation.optionId || data.transportation.optionId <= 0) && data.travelInfo.withoutTransport !== true && data.travelInfo.specialCase !== true) {
            ctx.addIssue({
              code: zod.ZodIssueCode.custom,
              message: 'Veuillez sélectionner une gamme de siège',
              path: ['transportation','optionId']
            })
          }
        } else {
          if(!data.travelInfo.customStartDate){
            ctx.addIssue({
              code: zod.ZodIssueCode.custom,
              message: 'Veuillez indiquer la date d\'arrivée',
              path: ['travelInfo','customStartDate']
            })
          }
          if(!data.travelInfo.customEndDate){
            ctx.addIssue({
              code: zod.ZodIssueCode.custom,
              message: 'Veuillez indiquer la date de retour',
              path: ['travelInfo','customEndDate']
            })
          }
        }
      }

      return zod.NEVER;
      }),
    zod.object({
      transfer: zod.object({
        outboundId: zod.number().nullish(),
        returnId: zod.number().nullish(),
        roundTripId: zod.number().nullish(),
      }).optional()
    }),
    zod.object({
      hotelRooms: zod.array(zod.object({
        id: zod.number().nonnegative(),
        type: zod.nativeEnum(TravelDbHotelRoomTypeEnum, {message: 'Le type de chambre est obligatoire' }),
        roomId: zod.number({message: 'Veuillez choisir une gamme pour chaque chambre' }).min(1, {message: 'Veuillez choisir une gamme pour chaque chambre' }),
        roommates:
          zod.array(zod.object({
            companionId: zod.number().nonnegative().nullish(),
            userId: zod.number().nonnegative().nullish(),
          }).nullish()).nullish()
      })).optional()
      .superRefine((data, ctx) => {
        data?.forEach((room, idx) => {
          if(room.type == TravelDbHotelRoomTypeEnum.Single && room.roommates && room.roommates.length > 1) {
            ctx.addIssue({
              code: zod.ZodIssueCode.custom,
              message: 'Une chambre individuelle ne peut pas avoir de colocataire',
              path: [idx, 'roommates']
            });
          } else if(
               (room.type == TravelDbHotelRoomTypeEnum.Double)
            && ((room.roommates?.length ?? 0) < 2)) {
            ctx.addIssue({
              code: zod.ZodIssueCode.custom,
              message: 'Une chambre double doit etre composée de 2 occupants',
              path: [idx, 'roommates']
            });
          } else if(room.type == TravelDbHotelRoomTypeEnum.Famille && (room.roommates?.length ?? 0) < 3) {
            ctx.addIssue({
              code: zod.ZodIssueCode.custom,
              message: 'Une chambre famille doit avoir au moins 3 occupants',
              path: [idx, 'roommates']
            });
          }
        });

        const CompanionsCount = data?.reduce((acc, room) => acc + (room.roommates?.filter((r) => r && r.companionId != null).length ?? 0), 0) ?? 0;
        const CurrentParticipantInHotel = data?.some((room) => room.roommates?.some((r) => r && r.userId === userStore.currentUser?.id)) ?? false;

        if((CompanionsCount < (participation.value?.companions?.length ?? 0) ) || !CurrentParticipantInHotel ) {
          ctx.addIssue({
            code: zod.ZodIssueCode.custom,
            message: `Tous les ${GetCompanionLabel(participation.value?.travel.companionLabel, 2)} doivent être assignés à une chambre`,
            path: ['hotelRooms']
          })
        }
      }),
    }),
    zod.object({
      options: zod.array(zod.object({
        id: zod.number().nonnegative(),
        activities: zod.array(zod.object({
          id: zod.number(),
          participantCount: zod.number().min(0)
        })).optional()
      })).optional()
    }),
    zod.object({
      documents: zod.array(zod.object({
        id: zod.number().nonnegative(),
        label: zod.string(),
        required: zod.boolean(),
        file: zod.object({
          key: zod.string(),
          mimeType: zod.string()
        }).optional()
      })).optional()
        .superRefine((data, ctx) => {
          data?.forEach((doc, idx) => {
            if (doc.required && (!doc.file || !doc.file.key)) {
              ctx.addIssue({
                code: zod.ZodIssueCode.custom,
                message: `Le document "${doc.label}" est obligatoire`,
                path: [idx, 'file']
              })
            }
          })
        }),
      companions: zod.array(zod.object({
        documents: zod.array(zod.object({
          id: zod.number().nonnegative(),
          label: zod.string(),
          required: zod.boolean(),
          file: zod.object({
            key: zod.string(),
            mimeType: zod.string()
          }).optional()
        })).optional()
          .superRefine((data, ctx) => {
            data?.forEach((doc, idx) => {
              if (doc.required && (!doc.file || !doc.file.key)) {
                ctx.addIssue({
                  code: zod.ZodIssueCode.custom,
                  message: `Le document "${doc.label}" est obligatoire`,
                  path: [idx, 'file']
                })
              }
            })
          })
      })).optional()
    }),
    zod.object({
      signature: zod.object({
        signedDate: zod.date().optional(),
      }).optional().refine((data) => {
        if((userStore.connectedUser?.isAdmin === true) && userStore.isImpersonated) { return true }
        else if(participation.value?.travel.firmaTemplateId) {
          return data?.signedDate != null;
        }
        return true;
      }, {message: 'La signature du document est obligatoire'})
    }),
    zod.object({
      userInfo: zod.object({
        specialRequests: zod.string().optional(),
        allergies: zod.string().optional(),
      }).optional()
    }),
]

const currentStepIndex = computed(() => {
  return Math.max(step.value, 0);
});

const currentStep = computed(() => {
  return steps.value[currentStepIndex.value];
});

const currentSchema = computed(() => {
  return toTypedSchema(schemas.length > currentStepIndex.value ? schemas[currentStepIndex.value]! : zod.object({}));
});

const paymentSucceeded = computed(() => {
  return participationValue?.payment?.status === PaymentStatusEnum.Succeeded;
});

const paymentLocked = computed(() => {
  return paymentRequired.value && paymentSucceeded.value && currentStep.value?.key === 'payment';
});

const canFinalize = computed(() => {
  return !paymentRequired.value || paymentSucceeded.value;
});

const canNavigateToStep = (index: number) => {
  if (paymentLocked.value && index < currentStepIndex.value) {
    return false;
  }

  return index < currentStepIndex.value || validatedSteps.value.has(index) || validatedSteps.value.has(index - 1) || participation.value?.invitation?.completionDate != null;
}

const {
  handleSubmit,
  isSubmitting,
  resetForm,
  setFieldValue,
  values: participationValue,
} = useForm<UserParticipation>({
  validationSchema: currentSchema,
  keepValuesOnUnmount: true,
  validateOnMount: false,
});

const currentStepIsValid = async () => {
  return await handleSubmit(() => true,(result) => {
      const errors = [...new Set(Object.keys(result.errors)
      .map((key:string) => {
        return result.errors[key as keyof typeof result.errors]
      }))];

      notificationsStore.notifyError(errors.slice(0, 5).join('<br>'));
      return false;
  })();
};

const currentPage = computed(() => {
  if(currentStep.value?.key?.startsWith('page-')) {
    const pageId = parseInt(currentStep.value.key.substring(5));
    return travelConfigStore.travelPages.find(page => page.id === pageId)
  }
  return null;
})

const navStepChanged = async (index: number) => {
  if(index > step.value) {
    if(!await currentStepIsValid()) return;
  }
  step.value = index;
};

const nextStep = async () => {
  if(!await currentStepIsValid()) return;

  if (isLastStep.value && !canFinalize.value) {
    notificationsStore.notifyError('Veuillez confirmer le paiement avant de finaliser votre inscription.');
    return;
  }

  if(isLastStep.value && participationValue?.invitation) {
      setFieldValue('invitation.completionDate', new Date());
  }

  const saved = await save()
  if(saved) {
    if(isLastStep.value) {
      router.replace({ name: 'home' });
      return;
    }
    validatedSteps.value.add(currentStepIndex.value);
    const nextActiveStepIndex = steps.value.findIndex((s, idx) => idx > currentStepIndex.value && (s.active ?? true));
    step.value = Math.min(nextActiveStepIndex, steps.value.length - 1)
    ScrollToTop()
    setTimeout(() => { scrollDownIndicator.value?.checkScrollability(); }, 500)
  }
};

const previousStep = async () => {
  if (paymentLocked.value) return;

  if(!await currentStepIsValid()) return;

  const saved = await save()
  if(saved) {
    if(isFirstStep.value) {
      return;
    }
    const previousActiveStepIndex = steps.value.findLastIndex((s, idx) => idx < currentStepIndex.value && (s.active ?? true));
    step.value = Math.max(previousActiveStepIndex, 0)
    ScrollToTop()
    setTimeout(() => { scrollDownIndicator.value?.checkScrollability(); }, 500)
  }
};

const isLastStep = computed(() => {
  return currentStepIndex.value == steps.value.findLastIndex(x => (x.active??true) == true);
});

const isFirstStep = computed(() => {
  return currentStepIndex.value == steps.value.findIndex(x => (x.active??true) == true);
});

const save = async () => {
  return await handleSubmit(async () => {
    if(participation.value?.travel.id == undefined) {
      return false;
    }
    saveInProgress.value = true;
    return await travelApi.saveParticipation(participation.value.travel.id,participationValue)
    .then((response) => {
        resetForm({values: response.data}, { force: true });
        participation.value = response.data;
        return true;
    }).catch((error: Error) => {
      notificationsStore.notifyError('Erreur lors de l\'enregistrement',error.message);
      return false;
    });
  },(result) => {
      const errors = [...new Set(Object.keys(result.errors)
      .map((key:string) => {
        return result.errors[key as keyof typeof result.errors]
      }))];
      notificationsStore.notifyError(errors.slice(0, 5).join('<br>'));
      return false;
  })().finally(() => {
    saveInProgress.value = false;
  });
}

const goHome = () => {
  router.push({ name: 'home' });
};

const logo = computed(() => {
  if (participation.value?.travel.logo?.key) {
    return getPictureUrl(participation.value.travel.logo.key);
  }

  return undefined;
});

onMounted(() => {
  if(props.id) {
    travelApi.getParticipation(props.id).then((response) => {
      participation.value = response.data;
      resetForm({values: response.data}, { force: true });
      if (paymentRequired.value && response.data.payment?.status === PaymentStatusEnum.Succeeded
        && !(userStore.connectedUser?.isAdmin == true)
      ) {
        step.value = steps.value.findIndex((s) => s.key === 'payment');
      }
      if (response.data.travel.id) { travelConfigStore.setTravel(response.data.travel.id); }
    }).catch((error: Error) => {
      notificationsStore.notifyError('Erreur lors du chargement de la page',error.message);
      router.push({ name: 'home' });
    }).finally(() => {
      loading.value = false;
    });
  } else if(props.slug) {
    travelApi.getParticipationBySlug(props.slug).then((response) => {
      participation.value = response.data;
      resetForm({values: response.data}, { force: true });
      if (paymentRequired.value && response.data.payment?.status === PaymentStatusEnum.Succeeded
        && !(userStore.connectedUser?.isAdmin == true)
    ) {
        console.log('Navigating to payment step');
        step.value = steps.value.findIndex((s) => s.key === 'payment');
      }
      if (response.data.travel.id) { travelConfigStore.setTravel(response.data.travel.id); }
    }).catch((error: Error) => {
      notificationsStore.notifyError('Erreur lors du chargement de la page',error.message);
      router.push({ name: 'home' });
    }).finally(() => {
      loading.value = false;
    });
  } else {
    notificationsStore.notifyError('Aucun séjour trouvé', 'Identifiant de séjour manquant');
    router.push({ name: 'home' });
  }
});
</script>
<style lang="css" scoped>
.flag-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  background: #fff;
}

.flag-wrapper .fi {
  width: 48px;
  height: 36px;
  border-radius: 6px;
  background-size: cover;
}
</style>
